// CountryScene3D.js  — v2 quality pass
// Pure React Three Fiber animated 3D scenes for Japan, Egypt, Kenya.
// No external files — all procedural geometry animated in useFrame.

import { useFrame } from '@react-three/fiber';
import { useMemo, useRef } from 'react';
import * as THREE from 'three';

const _d = new THREE.Object3D(); // shared dummy for instancedMesh

// ─── helpers ──────────────────────────────────────────────────────────────────

function Ground({ r = 2.8, color = '#c8a840' }) {
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.001, 0]}>
      <circleGeometry args={[r, 40]} />
      <meshStandardMaterial color={color} roughness={1} />
    </mesh>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// JAPAN
// ══════════════════════════════════════════════════════════════════════════════

function SakuraPetals({ count = 300 }) {
  const ref = useRef();
  const data = useMemo(() =>
    Array.from({ length: count }, () => ({
      x:  (Math.random() - 0.5) * 5,
      y:   0.4 + Math.random() * 3,
      z:  (Math.random() - 0.5) * 5,
      vy: -(0.22 + Math.random() * 0.40),
      ph:  Math.random() * Math.PI * 2,
      sp:  0.4 + Math.random() * 1.2,
      rx:  Math.random() * Math.PI * 2,
      rz:  Math.random() * Math.PI * 2,
      rxv: (Math.random() - 0.5) * 4,
      rzv: (Math.random() - 0.5) * 3,
      w:   0.07 + Math.random() * 0.05,
      h:   0.04 + Math.random() * 0.03,
    })),
  [count]);

  useFrame((s, dt) => {
    if (!ref.current) return;
    const t = s.clock.elapsedTime;
    data.forEach((p, i) => {
      p.y += p.vy * dt;
      p.x += Math.sin(t * p.sp + p.ph) * dt * 0.18;
      p.z += Math.cos(t * p.sp * 0.8 + p.ph) * dt * 0.12;
      if (p.y < 0) { p.y = 3.2 + Math.random() * 0.5; p.x = (Math.random()-0.5)*5; p.z = (Math.random()-0.5)*5; }
      p.rx += p.rxv * dt;  p.rz += p.rzv * dt;
      _d.position.set(p.x, p.y, p.z);
      _d.rotation.set(p.rx, 0, p.rz);
      _d.scale.set(p.w, 0.002, p.h);
      _d.updateMatrix();
      ref.current.setMatrixAt(i, _d.matrix);
    });
    ref.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh ref={ref} args={[null, null, count]}>
      <circleGeometry args={[1, 5]} />
      <meshStandardMaterial color="#ffb7c5" side={THREE.DoubleSide}
        emissive="#ff88aa" emissiveIntensity={0.12} />
    </instancedMesh>
  );
}

function SakuraTree({ x = 0, z = 0, h = 1.5, s = 1.0 }) {
  const pink = '#ffb3c6';
  const bark = '#5c3317';
  return (
    <group position={[x, 0, z]}>
      <mesh position={[0, h * 0.36, 0]} rotation={[0.04, 0, 0.07]}>
        <cylinderGeometry args={[0.11, 0.18, h * 0.72, 8]} />
        <meshStandardMaterial color={bark} roughness={1} />
      </mesh>
      {/* branch left */}
      <mesh position={[-0.1, h * 0.62, 0.05]} rotation={[0, 0, -0.32]}>
        <cylinderGeometry args={[0.06, 0.09, h * 0.36, 6]} />
        <meshStandardMaterial color={bark} roughness={1} />
      </mesh>
      {/* branch right */}
      <mesh position={[0.1, h * 0.60, -0.04]} rotation={[0, 0, 0.28]}>
        <cylinderGeometry args={[0.055, 0.08, h * 0.30, 6]} />
        <meshStandardMaterial color={bark} roughness={1} />
      </mesh>
      {/* blossom clouds */}
      {[
        [ 0,    h*1.06,  0,    s*0.76],
        [-s*.52, h*1.18, 0.22, s*0.58],
        [ s*.48, h*1.14,-0.18, s*0.58],
        [ 0.08, h*1.36,  0.30, s*0.50],
        [-0.28, h*1.28, -0.24, s*0.46],
        [ 0.42, h*1.42,  0.10, s*0.40],
        [-0.12, h*1.54,  0.08, s*0.36],
        [ 0,    h*0.90,  0.40, s*0.44],
        [ 0.50, h*1.00,  0.28, s*0.38],
      ].map(([bx,by,bz,r],i) => (
        <mesh key={i} position={[bx,by,bz]}>
          <sphereGeometry args={[r, 9, 7]} />
          <meshStandardMaterial color={pink} roughness={0.8}
            emissive="#ff88aa" emissiveIntensity={0.08} />
        </mesh>
      ))}
    </group>
  );
}

function ToriiGate({ x = 1.9, z = -0.5 }) {
  const red = '#c41e00';
  return (
    <group position={[x, 0, z]} rotation={[0, 0.35, 0]}>
      {[-0.44, 0.44].map((px, i) => (
        <mesh key={i} position={[px, 0.8, 0]}>
          <cylinderGeometry args={[0.078, 0.095, 1.6, 8]} />
          <meshStandardMaterial color={red} />
        </mesh>
      ))}
      {/* kasagi */}
      <mesh position={[0, 1.66, 0]}>
        <boxGeometry args={[1.26, 0.13, 0.20]} />
        <meshStandardMaterial color={red} />
      </mesh>
      {[-0.65, 0.65].map((px, i) => (
        <mesh key={i} position={[px, 1.70, 0]} rotation={[0,0, i===0? 0.20:-0.20]}>
          <boxGeometry args={[0.20, 0.10, 0.19]} />
          <meshStandardMaterial color={red} />
        </mesh>
      ))}
      {/* nuki crossbar */}
      <mesh position={[0, 1.28, 0]}>
        <boxGeometry args={[1.04, 0.09, 0.13]} />
        <meshStandardMaterial color={red} />
      </mesh>
    </group>
  );
}

function Pagoda({ x = -2.1, z = 0.9 }) {
  const roof = '#8b1a1a';
  const wall = '#f5e6c8';
  const tiers = [
    { y: 0.12, bw: 0.72, bh: 0.24, cw: 0.54, ch: 0.19 },
    { y: 0.55, bw: 0.52, bh: 0.20, cw: 0.40, ch: 0.17 },
    { y: 0.92, bw: 0.36, bh: 0.16, cw: 0.28, ch: 0.15 },
  ];
  return (
    <group position={[x, 0, z]}>
      {tiers.map((t, i) => (
        <group key={i}>
          <mesh position={[0, t.y, 0]}>
            <boxGeometry args={[t.bw, t.bh, t.bw]} />
            <meshStandardMaterial color={wall} />
          </mesh>
          <mesh position={[0, t.y + t.bh * 0.5 + t.ch * 0.5, 0]} rotation={[0, Math.PI/4, 0]}>
            <coneGeometry args={[t.cw, t.ch, 4]} />
            <meshStandardMaterial color={roof} />
          </mesh>
        </group>
      ))}
      {/* golden spire */}
      <mesh position={[0, 1.28, 0]}>
        <cylinderGeometry args={[0.018, 0.018, 0.44, 5]} />
        <meshStandardMaterial color="#d4a000" metalness={0.7} roughness={0.2} />
      </mesh>
    </group>
  );
}

export function JapanScene() {
  return (
    <group>
      <ambientLight intensity={0.55} color="#ffeaf2" />
      <directionalLight position={[4, 6, 3]} intensity={1.6} color="#fff4f8" />
      <pointLight position={[-2, 4, 1]} intensity={0.9} color="#ffccee" />
      <Ground r={2.9} color="#ffd6e8" />
      <SakuraPetals count={300} />
      <SakuraTree x={0}    z={0}   h={1.7} s={1.1} />
      <SakuraTree x={-2.1} z={0.6} h={1.4} s={0.88} />
      <ToriiGate  x={1.9}  z={-0.5} />
      <Pagoda     x={-2.1} z={0.9} />
    </group>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// EGYPT
// ══════════════════════════════════════════════════════════════════════════════

function Pyramid({ x, z, sc = 1 }) {
  return (
    <group position={[x, 0, z]}>
      <mesh position={[0, sc*0.66, 0]} rotation={[0, Math.PI/4, 0]}>
        <coneGeometry args={[sc*0.94, sc*1.32, 4]} />
        <meshStandardMaterial color="#d4a040" roughness={0.95} />
      </mesh>
      {/* base shadow strip */}
      <mesh position={[0, 0.008, 0]} rotation={[-Math.PI/2, 0, Math.PI/4]}>
        <planeGeometry args={[sc*1.85, sc*1.85]} />
        <meshStandardMaterial color="#a87828" roughness={1} />
      </mesh>
    </group>
  );
}

function PalmTree({ x, z, sc = 1 }) {
  return (
    <group position={[x, 0, z]}>
      <mesh position={[0, sc*0.62, 0]} rotation={[0.07, 0, 0.10]}>
        <cylinderGeometry args={[0.058*sc, 0.096*sc, sc*1.24, 7]} />
        <meshStandardMaterial color="#8b6914" roughness={0.9} />
      </mesh>
      {[0,1,2,3,4,5].map(i => {
        const a = (i/6)*Math.PI*2;
        return (
          <mesh key={i}
            position={[Math.cos(a)*0.24*sc, sc*1.32, Math.sin(a)*0.24*sc]}
            rotation={[0.55, a, 0]}>
            <boxGeometry args={[0.075*sc, 0.018*sc, 0.58*sc]} />
            <meshStandardMaterial color="#3a7a20" roughness={0.9} />
          </mesh>
        );
      })}
      {/* coconuts */}
      {[0,1,2].map(i => {
        const a = (i/3)*Math.PI*2;
        return (
          <mesh key={i} position={[Math.cos(a)*0.12*sc, sc*1.28, Math.sin(a)*0.12*sc]}>
            <sphereGeometry args={[0.06*sc, 6, 5]} />
            <meshStandardMaterial color="#6b4a10" roughness={0.8} />
          </mesh>
        );
      })}
    </group>
  );
}

function Camel() {
  const root = useRef();
  const fl = useRef(), fr = useRef(), bl = useRef(), br = useRef();
  const neck = useRef();

  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (root.current) {
      root.current.position.x = Math.sin(t * 0.72) * 1.3;
      root.current.position.y = Math.abs(Math.sin(t * 1.44)) * 0.045;
      root.current.rotation.y = Math.cos(t * 0.72) > 0 ? 0 : Math.PI;
    }
    const w = t * 1.44, sv = 0.55;
    if (fl.current) fl.current.rotation.x =  Math.sin(w) * sv;
    if (fr.current) fr.current.rotation.x = -Math.sin(w) * sv;
    if (bl.current) bl.current.rotation.x = -Math.sin(w) * sv;
    if (br.current) br.current.rotation.x =  Math.sin(w) * sv;
    if (neck.current) neck.current.rotation.x = Math.sin(w) * 0.07;
  });

  const C = '#c49a4e', D = '#8b6e30', F = '#d4aa66';
  const Eye = ({ pos }) => (
    <group position={pos}>
      <mesh><sphereGeometry args={[0.040,7,7]}/><meshStandardMaterial color="#111"/></mesh>
      <mesh position={[0.006, 0.008, 0.008]}><sphereGeometry args={[0.014,5,5]}/><meshStandardMaterial color="#fff" emissive="#fff" emissiveIntensity={0.6}/></mesh>
    </group>
  );

  return (
    <group ref={root} position={[0, 0.08, 0.3]}>
      {/* body */}
      <mesh position={[0, 0.48, 0]} scale={[1.32, 0.95, 0.88]}>
        <sphereGeometry args={[0.38, 10, 8]}/>
        <meshStandardMaterial color={C} roughness={0.85}/>
      </mesh>
      {/* hump */}
      <mesh position={[0.07, 0.80, 0]} scale={[1.0, 1.25, 0.9]}>
        <sphereGeometry args={[0.21, 9, 7]}/>
        <meshStandardMaterial color={F} roughness={0.85}/>
      </mesh>
      {/* neck */}
      <group ref={neck} position={[-0.34, 0.56, 0]}>
        <mesh rotation={[0, 0, -0.55]}>
          <cylinderGeometry args={[0.10, 0.13, 0.54, 8]}/>
          <meshStandardMaterial color={C} roughness={0.85}/>
        </mesh>
        {/* head */}
        <group position={[-0.32, 0.34, 0]}>
          <mesh scale={[1.22, 0.90, 0.96]}>
            <sphereGeometry args={[0.17, 9, 7]}/>
            <meshStandardMaterial color={C} roughness={0.85}/>
          </mesh>
          {/* snout */}
          <mesh position={[-0.15, -0.06, 0]} scale={[1.1, 0.72, 0.92]}>
            <sphereGeometry args={[0.10, 7, 6]}/>
            <meshStandardMaterial color={F} roughness={0.85}/>
          </mesh>
          <mesh position={[-0.22, -0.06,  0.045]}><sphereGeometry args={[0.026,5,4]}/><meshStandardMaterial color={D}/></mesh>
          <mesh position={[-0.22, -0.06, -0.045]}><sphereGeometry args={[0.026,5,4]}/><meshStandardMaterial color={D}/></mesh>
          <Eye pos={[-0.13, 0.07,  0.13]} />
          <Eye pos={[-0.13, 0.07, -0.13]} />
          {/* ears */}
          {[0.13, -0.13].map((ez, i) => (
            <mesh key={i} position={[0.04, 0.16, ez]} rotation={[0.2, 0, i===0? 0.3:-0.3]}>
              <sphereGeometry args={[0.055,6,5]}/><meshStandardMaterial color={D}/>
            </mesh>
          ))}
        </group>
      </group>
      {/* tail */}
      <mesh position={[0.44, 0.46, 0]} rotation={[0, 0, 0.6]}>
        <cylinderGeometry args={[0.022, 0.010, 0.28, 5]}/>
        <meshStandardMaterial color={D}/>
      </mesh>
      {/* legs */}
      {[{r:fl,px:-0.22,pz: 0.17},{r:fr,px:-0.22,pz:-0.17},{r:bl,px:0.22,pz:0.17},{r:br,px:0.22,pz:-0.17}]
        .map(({r,px,pz},i) => (
          <group key={i} ref={r} position={[px, 0.34, pz]}>
            <mesh position={[0,-0.17,0]}><cylinderGeometry args={[0.075,0.060,0.34,7]}/><meshStandardMaterial color={C} roughness={0.85}/></mesh>
            <mesh position={[0,-0.38,0]}><cylinderGeometry args={[0.058,0.044,0.27,6]}/><meshStandardMaterial color={D} roughness={0.85}/></mesh>
            <mesh position={[0,-0.55,0]} scale={[1.3,0.55,1.5]}><sphereGeometry args={[0.065,7,5]}/><meshStandardMaterial color={D} roughness={0.85}/></mesh>
          </group>
        ))}
    </group>
  );
}

export function EgyptScene() {
  return (
    <group>
      <ambientLight intensity={0.60} color="#ffe8b0" />
      <directionalLight position={[5, 7, 2]} intensity={2.2} color="#ffe8a0" />
      <pointLight position={[-3, 2, 2]} intensity={0.6} color="#ff9900" />
      <Ground r={3.1} color="#c49030" />
      {/* dune swell */}
      <mesh position={[1.2, 0.09, -1.6]} scale={[2.0, 0.28, 1.1]}>
        <sphereGeometry args={[1, 8, 5]}/>
        <meshStandardMaterial color="#d4a040" roughness={1}/>
      </mesh>
      <Pyramid x={-0.2} z={0}   sc={1.12} />
      <Pyramid x={-1.65} z={0.4} sc={0.82} />
      <Pyramid x={ 1.35} z={0.6} sc={0.62} />
      <PalmTree x={2.1} z={-0.8} sc={1.0} />
      <PalmTree x={2.4} z={ 0.5} sc={0.85} />
      <Camel />
    </group>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// KENYA
// ══════════════════════════════════════════════════════════════════════════════

function AcaciaTree({ x, z, sc = 1 }) {
  return (
    <group position={[x, 0, z]}>
      <mesh position={[0, sc*0.56, 0]} rotation={[0.05, 0, 0.08]}>
        <cylinderGeometry args={[0.055*sc, 0.10*sc, sc*1.12, 7]}/>
        <meshStandardMaterial color="#6b4a28" roughness={0.95}/>
      </mesh>
      {/* wide flat canopy layers */}
      <mesh position={[0, sc*1.14, 0]} scale={[2.25, 0.28, 2.25]}>
        <sphereGeometry args={[sc*0.62, 10, 6]}/>
        <meshStandardMaterial color="#3d6e2a" roughness={0.9}/>
      </mesh>
      <mesh position={[0.14*sc, sc*1.24, 0.10*sc]} scale={[1.75, 0.22, 1.75]}>
        <sphereGeometry args={[sc*0.48, 9, 5]}/>
        <meshStandardMaterial color="#4f8c38" roughness={0.9}/>
      </mesh>
      {[-0.30, 0.30].map((ox, i) => (
        <mesh key={i} position={[ox*sc, sc*1.30, (i===0?-0.20:0.16)*sc]} scale={[1.0, 0.28, 1.0]}>
          <sphereGeometry args={[sc*0.28, 7, 5]}/>
          <meshStandardMaterial color="#5aaa42" roughness={0.9}/>
        </mesh>
      ))}
    </group>
  );
}

function InstancedGrass({ count = 180 }) {
  const ref = useRef();
  const data = useMemo(() =>
    Array.from({ length: count }, () => ({
      x:  (Math.random()-0.5)*5.8,
      z:  (Math.random()-0.5)*5.8,
      ph: Math.random()*Math.PI*2,
      h:  0.10 + Math.random()*0.14,
      ry: Math.random()*Math.PI,
    })),
  [count]);

  useFrame((s) => {
    if (!ref.current) return;
    const t = s.clock.elapsedTime;
    data.forEach((g, i) => {
      _d.position.set(g.x, g.h*0.5, g.z);
      _d.rotation.set(0, g.ry, Math.sin(t*1.3+g.ph)*0.18);
      _d.scale.set(0.034, g.h, 0.018);
      _d.updateMatrix();
      ref.current.setMatrixAt(i, _d.matrix);
    });
    ref.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh ref={ref} args={[null, null, count]}>
      <boxGeometry args={[1,1,1]}/>
      <meshStandardMaterial color="#8aaa40" roughness={0.9}/>
    </instancedMesh>
  );
}

function Giraffe({ x = 1.7, z = -0.5 }) {
  const bodyRef = useRef();
  const neckRef = useRef();

  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (bodyRef.current) bodyRef.current.rotation.z = Math.sin(t*0.5)*0.025;
    if (neckRef.current) neckRef.current.rotation.x = Math.sin(t*0.42+0.5)*0.04;
  });

  const base = '#e8c060', spot = '#c8822a';

  const Eye = ({ pos }) => (
    <group position={pos}>
      <mesh><sphereGeometry args={[0.028,6,6]}/><meshStandardMaterial color="#111"/></mesh>
      <mesh position={[0.005,0.007,0.006]}><sphereGeometry args={[0.010,5,5]}/><meshStandardMaterial color="#fff" emissive="#fff" emissiveIntensity={0.6}/></mesh>
    </group>
  );

  return (
    <group position={[x, 0, z]}>
      <group ref={bodyRef}>
        {/* body */}
        <mesh position={[0, 0.56, 0]} scale={[1.12, 0.82, 0.78]}>
          <sphereGeometry args={[0.32, 9, 7]}/>
          <meshStandardMaterial color={base} roughness={0.8}/>
        </mesh>
        {/* spots */}
        {[[-0.10,0.60,0.24],[0.12,0.50,0.22],[-0.04,0.70,-0.20],[0.11,0.44,-0.18],[-0.14,0.50,-0.06]].map(([sx,sy,sz],i)=>(
          <mesh key={i} position={[sx,sy,sz]}>
            <sphereGeometry args={[0.068,5,4]}/><meshStandardMaterial color={spot} roughness={0.8}/>
          </mesh>
        ))}
        {/* legs */}
        {[[-0.15,0.2],[-0.15,-0.2],[0.15,0.2],[0.15,-0.2]].map(([lx,lz],i)=>(
          <mesh key={i} position={[lx, 0.28, lz]}>
            <cylinderGeometry args={[0.038,0.032,0.58,6]}/><meshStandardMaterial color={base} roughness={0.8}/>
          </mesh>
        ))}
        {/* hooves */}
        {[[-0.15,-0.02],[-0.15,-0.22],[0.15,-0.02],[0.15,-0.22]].map(([lx,lz],i)=>(
          <mesh key={i} position={[lx, 0.0, lz]}>
            <sphereGeometry args={[0.042,6,5]}/><meshStandardMaterial color={spot} roughness={0.9}/>
          </mesh>
        ))}
        {/* tail */}
        <mesh position={[0.28, 0.52, 0]} rotation={[0,0,0.7]}>
          <cylinderGeometry args={[0.014,0.006,0.28,5]}/><meshStandardMaterial color={spot}/>
        </mesh>
      </group>
      {/* long neck */}
      <group ref={neckRef} position={[-0.19, 0.72, 0]}>
        <mesh rotation={[0, 0, -0.18]}>
          <cylinderGeometry args={[0.065, 0.090, 1.02, 7]}/>
          <meshStandardMaterial color={base} roughness={0.8}/>
        </mesh>
        {/* neck spots */}
        {[[0,0.1,0.07],[0,-0.1,-0.06],[0,0.3,0.05]].map(([nx,ny,nz],i)=>(
          <mesh key={i} position={[nx,ny,nz]}>
            <sphereGeometry args={[0.052,5,4]}/><meshStandardMaterial color={spot} roughness={0.8}/>
          </mesh>
        ))}
        {/* head */}
        <group position={[-0.20, 0.60, 0]}>
          <mesh scale={[1.30, 0.78, 0.88]}>
            <sphereGeometry args={[0.13, 8, 6]}/>
            <meshStandardMaterial color={base} roughness={0.8}/>
          </mesh>
          {[0.06,-0.06].map((oz,i)=>(
            <mesh key={i} position={[0, 0.14, oz]}>
              <cylinderGeometry args={[0.014,0.010,0.20,5]}/><meshStandardMaterial color={spot}/>
            </mesh>
          ))}
          <Eye pos={[-0.10, 0.03,  0.09]} />
          <Eye pos={[-0.10, 0.03, -0.09]} />
        </group>
      </group>
    </group>
  );
}

function Elephant() {
  const root = useRef();
  const fl = useRef(), fr = useRef(), bl = useRef(), br = useRef();
  const trunk = useRef();
  const earL = useRef(), earR = useRef();

  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (root.current) {
      root.current.position.x = Math.sin(t * 0.56) * 1.0;
      root.current.position.y = Math.abs(Math.sin(t * 1.12)) * 0.05;
      root.current.rotation.y = Math.cos(t * 0.56) > 0 ? 0 : Math.PI;
    }
    const w = t * 1.12, sv = 0.48;
    if (fl.current) fl.current.rotation.x =  Math.sin(w) * sv;
    if (fr.current) fr.current.rotation.x = -Math.sin(w) * sv;
    if (bl.current) bl.current.rotation.x = -Math.sin(w) * sv;
    if (br.current) br.current.rotation.x =  Math.sin(w) * sv;
    if (trunk.current) {
      trunk.current.rotation.x = -0.25 + Math.sin(t * 0.85) * 0.38;
      trunk.current.rotation.z =  Math.sin(t * 0.65) * 0.18;
    }
    if (earL.current) earL.current.rotation.y =  0.1 + Math.sin(t * 1.6) * 0.38;
    if (earR.current) earR.current.rotation.y = -0.1 - Math.sin(t * 1.6) * 0.38;
  });

  const B = '#7a6a50', D = '#5a4e3c', S = '#8a7860';

  const Eye = ({ pos }) => (
    <group position={pos}>
      <mesh><sphereGeometry args={[0.044,7,7]}/><meshStandardMaterial color="#111"/></mesh>
      <mesh position={[0.006,0.009,0.008]}><sphereGeometry args={[0.016,5,5]}/><meshStandardMaterial color="#fff" emissive="#fff" emissiveIntensity={0.7}/></mesh>
    </group>
  );

  return (
    <group ref={root} position={[-0.6, 0.08, 0.2]}>
      <mesh position={[0, 0.53, 0]} scale={[1.22, 1.02, 0.92]}>
        <sphereGeometry args={[0.48, 12, 9]}/><meshStandardMaterial color={B} roughness={0.9}/>
      </mesh>
      <mesh position={[-0.44, 0.76, 0]} scale={[1.02, 1.06, 1.02]}>
        <sphereGeometry args={[0.30, 11, 9]}/><meshStandardMaterial color={B} roughness={0.9}/>
      </mesh>
      <mesh position={[-0.54, 0.90, 0]}>
        <sphereGeometry args={[0.19, 8, 7]}/><meshStandardMaterial color={S} roughness={0.9}/>
      </mesh>
      <group ref={trunk} position={[-0.70, 0.69, 0]}>
        <mesh position={[0,-0.22,0]} rotation={[0.1,0,0]}>
          <cylinderGeometry args={[0.082,0.066,0.44,8]}/><meshStandardMaterial color={B} roughness={0.9}/>
        </mesh>
        <mesh position={[0.04,-0.51,0.07]} rotation={[0.85,0,0]}>
          <cylinderGeometry args={[0.066,0.046,0.32,7]}/><meshStandardMaterial color={D} roughness={0.9}/>
        </mesh>
        <mesh position={[0.05,-0.70,0.20]} rotation={[1.5,0,0]}>
          <sphereGeometry args={[0.050,7,6]}/><meshStandardMaterial color={D} roughness={0.9}/>
        </mesh>
      </group>
      {[[ 0.10, 0.4],[-0.10,-0.4]].map(([oz,ry],i)=>(
        <mesh key={i} position={[-0.67,0.61,oz]} rotation={[0.25,ry,-0.46]}>
          <cylinderGeometry args={[0.022,0.008,0.32,6]}/><meshStandardMaterial color="#f8f4e8" roughness={0.2} metalness={0.04}/>
        </mesh>
      ))}
      <group ref={earL} position={[-0.30,0.79, 0.34]}>
        <mesh scale={[0.46,0.76,0.13]}>
          <sphereGeometry args={[0.37,9,7]}/><meshStandardMaterial color={D} roughness={0.9} side={THREE.DoubleSide}/>
        </mesh>
      </group>
      <group ref={earR} position={[-0.30,0.79,-0.34]}>
        <mesh scale={[0.46,0.76,0.13]}>
          <sphereGeometry args={[0.37,9,7]}/><meshStandardMaterial color={D} roughness={0.9} side={THREE.DoubleSide}/>
        </mesh>
      </group>
      <Eye pos={[-0.67,0.82, 0.19]} />
      <Eye pos={[-0.67,0.82,-0.19]} />
      <mesh position={[0.44,0.58,0]} rotation={[0,0,0.9]}>
        <cylinderGeometry args={[0.022,0.008,0.32,5]}/><meshStandardMaterial color={D}/>
      </mesh>
      {[{r:fl,px:-0.27,pz: 0.26},{r:fr,px:-0.27,pz:-0.26},{r:bl,px:0.24,pz:0.26},{r:br,px:0.24,pz:-0.26}]
        .map(({r,px,pz},i)=>(
          <group key={i} ref={r} position={[px,0.32,pz]}>
            <mesh position={[0,-0.17,0]}><cylinderGeometry args={[0.10,0.086,0.36,8]}/><meshStandardMaterial color={B} roughness={0.9}/></mesh>
            <mesh position={[0,-0.40,0]}><cylinderGeometry args={[0.086,0.076,0.28,8]}/><meshStandardMaterial color={D} roughness={0.9}/></mesh>
            <mesh position={[0,-0.58,0]} scale={[1.22,0.54,1.42]}><sphereGeometry args={[0.09,8,6]}/><meshStandardMaterial color={D} roughness={0.9}/></mesh>
          </group>
        ))}
    </group>
  );
}

function SunsetSun() {
  const raysRef = useRef();
  useFrame((s) => {
    if (raysRef.current) raysRef.current.rotation.z = s.clock.elapsedTime * 0.28;
  });
  return (
    <group position={[2.9, 1.9, -2.6]}>
      <mesh>
        <sphereGeometry args={[0.48,14,12]}/>
        <meshStandardMaterial color="#ff8000" emissive="#ff5500" emissiveIntensity={1.0}/>
      </mesh>
      <group ref={raysRef}>
        {Array.from({length:8},(_,i)=>{
          const a=(i/8)*Math.PI*2;
          return (
            <mesh key={i} position={[Math.cos(a)*0.68,Math.sin(a)*0.68,0]} rotation={[0,0,a]}>
              <boxGeometry args={[0.058,0.30,0.04]}/>
              <meshStandardMaterial color="#ffaa00" emissive="#ff8800" emissiveIntensity={0.9}/>
            </mesh>
          );
        })}
      </group>
    </group>
  );
}


export function KenyaScene() {
  return (
    <group>
      <ambientLight intensity={0.55} color="#ffe0a0" />
      <directionalLight position={[3, 5, 1]} intensity={1.8} color="#ffd060" />
      <pointLight position={[3, 2, -2]} intensity={1.2} color="#ff7700" />
      <Ground r={3.3} color="#c8a840" />
      <InstancedGrass count={180} />
      <AcaciaTree x={-2.1} z={-0.6} sc={1.1} />
      <AcaciaTree x={ 2.1} z={ 0.3} sc={0.88} />
      <AcaciaTree x={ 0.4} z={ 1.9} sc={0.72} />
      <Giraffe x={1.7} z={-0.5} />
      <Elephant />
      <SunsetSun />
    </group>
  );
}

// ─── export map ───────────────────────────────────────────────────────────────

export const COUNTRY_SCENES = {
  Japan: JapanScene,
  Egypt: EgyptScene,
  Kenya: KenyaScene,
};
